import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { ThemeToggleButton } from "../components/common/ThemeToggleButton";
import UserDropdown from "../components/header/UserDropdown";
import NotificationBell from "../components/notifications/NotificationBell";
import LanguageSelector from "../components/common/LanguageSelector";
import { useAuth } from "../context/AuthContext";
import { useFamilyMode } from "../context/FamilyModeContext";
import { doc, onSnapshot, collection, query, where } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useTranslation } from "react-i18next";
import FamilySwitcher from "../components/groups/FamilySwitcher";
import { useNotifications } from "../hooks/useNotifications";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { useAndroidVersion } from "../hooks/useAndroidVersion";
import { useActiveFamily } from "../context/ActiveFamilyContext";
import { useFeatureAccess } from "../hooks/useFeatureAccess";
import { useFeatureUnlock } from "../hooks/useFeatureUnlock";
import { useMemberRole } from "../hooks/useMemberRole";
import { useHasFamily } from "../hooks/useHasFamily";
import { usePlanLimits } from "../hooks/usePlanLimits";
import { useFeatureGate } from "../hooks/useFeatureGate";
import FeatureBadge from "../components/subscription/FeatureBadge";
import { Phone } from "lucide-react";

const AppHeader: React.FC = () => {
  const { t } = useTranslation();

  const [isApplicationMenuOpen, setApplicationMenuOpen] = useState(false);
  const { isMobileOpen, toggleMobileSidebar } = useSidebar();
  const { user } = useAuth();
  const [planBadge, setPlanBadge] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { enabled: familyMode, openExitModal, enable: enableFamilyMode } = useFamilyMode();
  const inputRef = useRef<HTMLInputElement>(null);
  const { unreadCount } = useNotifications();
  const androidVersion = useAndroidVersion();
  const { activeFamily } = useActiveFamily();
  const { features } = useFeatureUnlock();
  const { isOwnerOrAdmin } = useMemberRole();
  const { hasFamily } = useHasFamily();
  const { isFeatureBlocked } = usePlanLimits();
  const { promptUpgrade } = useFeatureGate();

  // Desktop offset fijo - sidebar siempre colapsado (90px)
  const desktopOffset = (!familyMode && !isMobileOpen) ? 90 : 0;
  
  // Detectar Android antiguo (< 8) para aplicar fallback de gradient
  const isOldAndroid = androidVersion !== null && androidVersion < 8;


  const handleToggle = () => {
    // Solo toggle mobile - desktop sidebar siempre colapsado
    toggleMobileSidebar();
  };

  const toggleApplicationMenu = () => {
    setApplicationMenuOpen(!isApplicationMenuOpen);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  const isCallsBlocked = isFeatureBlocked('familyCalls' as any);

  const openCalls = () => {
    if (isCallsBlocked) {
      promptUpgrade('familyCalls' as any);
      return;
    }
    navigate('/calls');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === "k") {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setPlanBadge(null);
      return;
    }

    let unsub: (() => void) | null = null;
    const timer = setTimeout(() => {
      unsub = onSnapshot(
        doc(db, "users", user.uid),
        (snap) => {
          const data = snap.exists() ? (snap.data() as any) : null;
          const plan = data?.subscription?.plan as string | undefined;
          const end = data?.subscription?.currentPeriodEnd;
          let endStr: string | null = null;
          if (end?.seconds) {
            const d = new Date(end.seconds * 1000);
            endStr = `${(d.getMonth() + 1).toString().padStart(2, "0")}/${d.getFullYear()}`;
          }
          const planNames: Record<string, string> = {
            'free': t('subscription.plan.free', { defaultValue: 'Plan Gratuito' }) as string,
            'family': t('subscription.plan.family', { defaultValue: 'Plan Familiar' }) as string,
            'familiar': t('subscription.plan.family', { defaultValue: 'Plan Familiar' }) as string,
            'enterprise': t('subscription.plan.enterprise', { defaultValue: 'Plan Enterprise' }) as string
          };
          const displayName = plan ? (planNames[plan] || plan) : null;
          setPlanBadge(displayName ? `${displayName}${endStr ? ` · ${t("common.expires")} ${endStr}` : ""}` : null);
        },
        (error) => {
          setPlanBadge(null);
        }
      );
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (unsub) unsub();
    };
  }, [user?.uid, t]);

  // Detectar si estamos en un formulario (edit o new)
  const isInForm = location.pathname.includes('/edit') || location.pathname.includes('/new');
  
  // Detectar si estamos en el board del miembro
  const isInBoard = location.pathname === '/groups/board';
  
  // Detectar si estamos en el calendario
  const isInCalendar = location.pathname === '/groups/calendar';

  // Detectar si estamos en HomeFamiliar (tablero personal)
  const isInHomeFamiliar = location.pathname === '/groups/home-groups';

  // En modo grupo y HomeFamiliar, ocultar botón de atrás
  const shouldHideBackButton = familyMode && isInHomeFamiliar;

  // Mobile page title derived from route
  const mobileTitle = (() => {
    const p = location.pathname || "";
    // Groups section
    if (p.startsWith("/groups")) {
      if (p.startsWith("/groups/dashboard")) return t('dashboard.title', { defaultValue: t('common.tableropersonal') }) as string;
      if (p.startsWith("/groups/board")) return t('dashboard.familyBoard', { defaultValue: t('common.tablerogrupal') }) as string;
      if (p.startsWith("/groups/home-groups")) return t('dashboard.title', { defaultValue: t('common.tableropersonal') }) as string;
      if (p.startsWith("/groups/checklist")) return t('tasks.title', { defaultValue: t('common.tareas') }) as string;
      if (p.startsWith("/groups/activities")) return t('activities.title', { defaultValue: t('common.actividades') }) as string;
      if (p.startsWith("/groups/members")) return t('members.title', { defaultValue: t('groups.integrantes') }) as string;
      if (p.startsWith("/groups/groups")) return t('groups.title', { defaultValue: t('common.grupos') }) as string;
      if (p.startsWith("/groups/calendar")) return t('calendar.title', { defaultValue: t('common.calendario') }) as string;
      if (p.startsWith("/groups/users")) return t('users.title', { defaultValue: t('groups.usuarios') }) as string;
    }
    if (p.startsWith("/rewards")) return t('rewards.title', { defaultValue: t('common.recompensas') }) as string;
    if (p.startsWith("/templates")) return t('templates.library', { defaultValue: t('common.rutinas') }) as string;
    if (p.startsWith("/subscription")) return t('subscription.title', { defaultValue: t('common.suscripcion') }) as string;
    if (p.startsWith("/preferences")) return t('preferences.title', { defaultValue: t('common.preferencias') }) as string;
    if (p.startsWith("/profile")) return t('common.profile', { defaultValue: t('common.perfil') }) as string;
    return '';
  })();

  return (
    <header className="sticky top-0 w-full z-50">
      {/* Fondo con Wave - Solo Desktop */}
      <div 
        className="hidden nb:block absolute inset-x-0 top-0 pointer-events-none"
        style={{
          backgroundColor: '#AAD08C',
          height: '110px', // Altura total para cubrir el header + la onda
          clipPath: "path('M0,0 H3840 V64 C3200,90 2560,44 1920,66 C1280,90 640,44 0,66 L0,0 Z')",
          zIndex: -1
        }}
      />

      <div className="relative flex flex-col">
        {/* Header Content - Solo visible en Desktop */}
        <div className="hidden nb:flex h-16 items-center justify-between relative px-6 lg:px-8 z-10">
          {/* Logo */}
          <Link to="/" className="hidden lg:flex items-center gap-2">
            <img
              className="dark:hidden"
              src="/images/logo/logo.svg"
              alt={t('common.logo')}
              width={120}
              height={30}
            />
            <img
              className="hidden dark:block"
              src="/images/logo/logo-dark.svg"
              alt={t('common.logo')}
              width={120}
              height={30}
            />
          </Link>

          {/* Desktop actions */}
          <div className="hidden md:flex items-center gap-3">
            <NotificationBell />
            <FamilySwitcher />
            <UserDropdown hideChevronOnMobile={true} planBadge={planBadge} />
          </div>

          {/* Botón hamburguesa/navegación para móvil */}
          <button
            className={`nb:hidden relative flex items-center justify-center w-10 h-10 text-white transition-colors rounded-2xl ${
              familyMode || shouldHideBackButton ? 'hidden' : 'flex'
            }`}
            onClick={() => {
              // En modo grupo, siempre navegar a la pantalla anterior
              if (familyMode) {
                navigate(-1);
                return;
              }
              // Fuera de modo grupo, usar lógica existente
              if (isInForm) {
                navigate(-1);
              } else if (isInBoard) {
                localStorage.removeItem('active_member_id');
                localStorage.removeItem('active_member_name');
                navigate('/groups/home-groups');
              } else if (isInCalendar) {
                navigate(-1);
              } else {
                handleToggle();
              }
            }}
            aria-label={
              window.innerWidth < 1024 && (isInForm || isInBoard || isInCalendar) ? t("common.back") : t("common.toggleSidebar")
            }
          >
              {(isInForm || isInBoard || isInCalendar) ? (
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : isMobileOpen ? (
                <svg width={24} height={24} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M6.21967 7.28131C5.92678 6.98841 5.92678 6.51354 6.21967 6.22065C6.51256 5.92775 6.98744 5.92775 7.28033 6.22065L11.999 10.9393L16.7176 6.22078C17.0105 5.92789 17.4854 5.92788 17.7782 6.22078C18.0711 6.51367 18.0711 6.98855 17.7782 7.28144L13.0597 12L17.7782 16.7186C18.0711 17.0115 18.0711 17.4863 17.7782 17.7792C17.4854 18.0721 17.0105 18.0721 16.7176 17.7792L11.999 13.0607L7.28033 17.7794C6.98744 18.0722 6.51256 18.0722 6.21967 17.7794C5.92678 17.4865 5.92678 17.0116 6.21967 16.7187L10.9384 12L6.21967 7.28131Z"
                    fill="currentColor"
                  />
                </svg>
              ) : (
                <svg width={20} height={16} viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" clipRule="evenodd" d="M0 2C0 1.44772 0.447715 1 1 1H19C19.5523 1 20 1.44772 20 2C20 2.55228 19.5523 3 19 3H1C0.447715 3 0 2.55228 0 2ZM0 8C0 7.44772 0.447715 7 1 7H19C19.5523 7 20 7.44772 20 8C20 8.55228 19.5523 9 19 9H1C0.447715 9 0 8.55228 0 8ZM1 13C0.447715 13 0 13.4477 0 14C0 14.5523 0.447715 15 1 15H19C19.5523 15 20 14.5523 20 14C20 13.4477 19.5523 13 19 13H1Z" fill="currentColor"/>
                </svg>
              )}
              
              {unreadCount > 0 && !isInForm && !isInBoard && !isInCalendar && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
          </button>
        </div>
      </div>

      {/* Mobile header - Estilo Referencia EXACTA (Verde con bordes redondeados abajo) */}
      {!isMobileOpen && (
        <div className="nb:hidden select-none sticky top-0 z-[100]">
          <div className="h-20 bg-[#AAD08C] rounded-b-[40px] flex items-center px-6 relative">
            {/* Left action: back button */}
            {!shouldHideBackButton && (
              <button
                onClick={() => {
                  // En modo grupo, siempre navegar a la pantalla anterior
                  if (familyMode) {
                    navigate(-1);
                    return;
                  }
                  // Fuera de modo grupo, usar lógica existente
                  if (isInForm || isInBoard || isInCalendar) {
                    navigate(-1);
                  } else {
                    handleToggle();
                  }
                }}
                className="flex items-center justify-center w-12 h-12 -ml-2 rounded-full active:scale-90 transition-all text-[#055590] z-20"
                aria-label={familyMode || isInForm || isInBoard || isInCalendar ? t("common.back") : t("common.toggleSidebar")}
              >
                <svg width={28} height={28} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}

            {/* Center page title */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none px-16 z-10">
              <h1 className="truncate text-xl font-bold text-[#055590] tracking-tight text-center">
                {location.pathname === '/groups/home-groups' ? (activeFamily?.name || t('common.tableropersonal')) : (mobileTitle || activeFamily?.name || t('common.tableropersonal'))}
              </h1>
            </div>
          </div>
        </div>
      )}

    </header>
  );
};

export default AppHeader;
